import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Privacy Policy - PokeCardIndex</title>
        <meta name="description" content="Privacy Policy for PokeCardIndex - Learn how we handle and protect your data in accordance with US privacy laws including CCPA." />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>

        <div className="space-y-8 text-white/80">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Introduction</h2>
            <p>
              This Privacy Policy explains how PokeCardIndex ("we", "us", "our"), based in the United States, collects, uses, 
              and protects your personal information when you use our website. This policy is designed to comply with US privacy 
              laws, including the California Consumer Privacy Act (CCPA) and other state privacy regulations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Information We Collect</h2>
            <p className="mb-4">Under the CCPA and other US privacy laws, personal information is any information that 
            identifies, relates to, describes, or could reasonably be linked with you or your household.</p>
            
            <h3 className="text-lg font-medium text-white mb-2">Account Information</h3>
            <p className="mb-4">When you create an account, we collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Email address</li>
              <li>Username</li>
              <li>Password (encrypted)</li>
              <li>Optional profile information (e.g., avatar)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-2">Usage Data</h3>
            <p className="mb-4">Through Google Analytics, we collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>Browser type and version</li>
              <li>Device type and screen size</li>
              <li>Referring website</li>
              <li>IP address (anonymized in compliance with US privacy laws)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">How We Use Your Information</h2>
            <p className="mb-4">As required by US privacy laws, we disclose all the ways we use your personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain your user account</li>
              <li>To authenticate you when you sign in</li>
              <li>To save your preferences and settings</li>
              <li>To analyze site usage and improve our services</li>
              <li>To communicate important updates about our service</li>
              <li>To prevent fraud and abuse</li>
              <li>To comply with legal obligations</li>
              <li>To respond to law enforcement requests as required by applicable law</li>
            </ul>
            
            <p className="mt-4">We do not sell your personal information as defined under the CCPA or other US privacy laws.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Cookies</h2>
            <p className="mb-4">
              We use cookies to enhance your experience on our website. In accordance with US privacy laws, we provide clear 
              notice about our use of cookies and allow you to opt-out of non-essential cookies.
            </p>
            
            <h3 className="text-lg font-medium text-white mb-2">Types of Cookies We Use:</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Essential Cookies:</strong> Required for basic site functionality and authentication
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website (Google Analytics)
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remember your settings and choices
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Data Storage and Security</h2>
            <p className="mb-4">
              We use Supabase, with servers located in the United States, to store and process your data. All data is encrypted 
              in transit and at rest using industry-standard encryption methods. Your password is never stored in plain text 
              and is securely hashed using industry-standard algorithms.
            </p>
            <p>
              We implement appropriate security measures to protect against unauthorized access, alteration,
              disclosure, or destruction of your personal information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Your Privacy Rights</h2>
            <p className="mb-4">Under the CCPA and other US privacy laws, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Delete your personal information (with certain exceptions as required by law)</li>
              <li>Object to our processing of your data</li>
              <li>Export your data</li>
              <li>Withdraw consent for cookies and analytics</li>
              <li>Non-discrimination for exercising your privacy rights</li>
              <li>Opt-out of the sale of personal information (although we do not sell personal information)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-white mt-6 mb-2">California Residents</h3>
            <p className="mb-4">
              If you are a California resident, you have additional rights under the CCPA, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The right to know what personal information we collect and how we use it</li>
              <li>The right to access your personal information in a portable format</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt-out of the sale of your personal information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Contact Us</h2>
            <p>
              For questions about this Privacy Policy, to exercise your privacy rights, or to submit a CCPA request, please contact us at{' '}
              <a href="mailto:privacy@pokecardindex.com" className="underline hover:text-white">
                privacy@pokecardindex.com
              </a>
            </p>
            <p className="mt-4">
              Response Time: We will respond to privacy-related requests within 45 days, as required by the CCPA.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
              We will notify you of any material changes by posting the new Privacy Policy on this page and updating the 
              effective date below. Your continued use of our services after such modifications constitutes your acknowledgment 
              of the modified Privacy Policy and your agreement to abide and be bound by it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Children's Privacy</h2>
            <p>
              Our service is not directed to children under 13. In compliance with COPPA (Children's Online Privacy Protection 
              Act), we do not knowingly collect personal information from children under 13. If you are a parent or guardian 
              and believe we may have collected information from your child, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Do Not Track Signals</h2>
            <p>
              Some browsers have a "Do Not Track" feature that signals to websites that you do not want to have your online 
              activities tracked. Our website responds to Do Not Track signals and does not track, plant cookies, or use 
              advertising when a Do Not Track browser mechanism is in place.
            </p>
          </section>

          <p className="text-sm text-white/60 pt-8">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
}